import { h, resolveComponent } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { ActiveUser } from '@/services/user';

const Home = () => import('@/views/home/Home')

const Login = () => import('@/views/pages/Login')
const Form = () => import('@/views/forms/Form')
const FormAnswers = () => import('@/views/forms/FormAnswers')
const FormAnswersAsCards = () => import('@/views/forms/FormAnswersAsCards')
const FormCreate = () => import('@/views/forms/FormCreate')
const Forms = () => import('@/views/forms/Forms')

const Group = () => import('@/views/groups/Group')

const TheContainer = () => import('@/containers/TheContainer')
const UserView = () => import('@/views/users/UserView');
const UsersView = () => import('@/views/users/UsersView');
const Page404 = () => import('@/views/pages/Page404');

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
});

const isOpenAccess = (route) =>
  route.matched.some((route) => route.meta?.isOpenAccess);

const requiresAdmin = (requestedRoute) =>
  requestedRoute.matched.some(
    (matchedRoute) => matchedRoute.meta?.requiresAdmin
  );

const requiresGroupManager = (requestedRoute) =>
  requestedRoute.matched.some(
    (matchedRoute) => matchedRoute.meta?.requiresGroupManager
  );

const requiresLastGroupManager = (requestedRoute) =>
  requestedRoute.matched[requestedRoute.matched.length - 1]?.meta?.requiresLastGroupManager;

const requiresGroupUser = (requestedRoute) =>
  requestedRoute.matched.some(
    (matchedRoute) => matchedRoute.meta?.requiresGroupUser
  );

const isFound = (route) => route.matched[0].name !== "NotFound";

router.beforeEach((to, from, next) => {
  const isAuthenticated = ActiveUser.get();
  if (!isAuthenticated && !isOpenAccess(to)) {
    if (isFound(to)) {
      localStorage.setItem('pathToLoadAfterLogin', to.path)
    }
    return next({name: 'Login' });
  }

  if (
    isAuthenticated && !ActiveUser.isSuperUser() && (
      requiresAdmin(to) ||
      (requiresGroupManager(to) && !ActiveUser.isGroupManager(to.params?.groupId, to.params?.id)) ||
      (requiresLastGroupManager(to) && !ActiveUser.isGroupManager(to.params?.groupId, to.params?.id)) ||
      (requiresGroupUser(to) && !ActiveUser.isGroupUser(to.params?.groupId, to.params?.id))
    )
  ) {
    return next({ name: "Home" });
  }
  return next()
});
export default router

function handleHideInBreadcrumb(route) {
  if (route.name === 'AnswerEdit') return true
  if (['FormGroupFill', 'FormsGroup'].includes(route.name)) return !ActiveUser.isGroupUser(route.params?.groupId, route.params?.id)
  return false
}

function configRoutes () {
  return [
    {
      path: '/oauth',
      name: 'OAuth',
      component: Login,
      meta: {
        isOpenAccess: true,
      },
    },
    {
      path: '/',
      meta: {
        label: 'Home',
      },
      component: TheContainer,
      children: [
        {
          name: 'Home',
          path: '',
          component: Home,
        },
        {
          path: 'forms',
          redirect: '/',
          meta: {
            hideInBreadcrumbs: true,
          },
          component: {
            render() {
              return h(resolveComponent('router-view'));
            },
          },
          children: [
            {
              path: 'create',
              name: 'FormCreate',
              meta: {
                label: 'Form Create',
                requiresAdmin: true,
              },
              component: FormCreate,
            },
            {
              path: ':id',
              name: 'FormId',
              meta: {
                paramKey: 'id',
                hideInBreadcrumbs: (route) => handleHideInBreadcrumb(route),
                isOpenAccess: true,
              },
              component: {
                render() {
                  return h(resolveComponent('router-view'));
                },
              },
              children: [
                {
                  path: 'edit',
                  name: 'FormEdit',
                  meta: {
                    label: 'Form Edit',
                    hideInBreadcrumbs: true
                  },
                  component: FormCreate,
                },
                {
                  path: 'answers',
                  name: 'FormAnswers',
                  meta: {
                    label: 'Form Answer',
                    requiresGroupManager: true,
                  },
                  component: FormAnswers
                },
                {
                  path: 'fill_in/:answerId',
                  name: "AnswerEdit",
                  meta: {
                    paramKey: 'answerId',
                  },
                  component: Form,
                },
                {
                  path: ':internalExternal/fill_in',
                  name: 'Form',
                  meta: {
                    isOpenAccess: true,
                    label: "Form Fill",
                  },
                  component: Form,
                },
                {
                  path: 'cards',
                  name: 'FormCards',
                  meta: {
                    requiresGroupManager: true,
                  },
                  component: FormAnswersAsCards,
                },
              ]
            },
          ],
        },
        {
          path: 'groups',
          redirect: '/',
          component: {
            render() {
              return h(resolveComponent('router-view'));
            },
          },
          meta: {
            hideInBreadcrumbs: true,
          },
          children: [
            {
              path: ':groupId',
              name: 'GroupId',
              meta: {
                paramKey: 'groupId',
                hideInBreadcrumbs: (route) => handleHideInBreadcrumb(route)
              },
              component: {
                render() {
                  return h(resolveComponent('router-view'));
                },
              },
              children: [
                {
                  name: 'Group',
                  path: '',
                  component: Group,
                },
                {
                  path: 'form',
                  name: '',
                  component: {
                    render() {
                      return h(resolveComponent('router-view'));
                    },
                  },
                  meta: {
                    hideInBreadcrumbs: true,
                  },
                  children: [
                    {
                      name: 'FormsGroup',
                      path: '',
                      meta: {
                        requiresGroupUser: true,
                      },
                      component: Forms,
                    },
                    {
                      path: 'create',
                      name: 'FormCreateGroup',
                      meta: {
                        label: 'Create Form',
                      },
                      component: FormCreate,
                    },
                    {
                      redirect: {name: 'FormEditGroup'},
                      path: ':id',
                      name: 'GroupFormId',
                      meta: {
                        hideInBreadcrumbs: (route) => handleHideInBreadcrumb(route),
                        paramKey: 'id',
                      },
                      component: {
                        render() {
                          return h(resolveComponent('router-view'));
                        },
                      },
                      children: [
                        {
                          path: 'edit',
                          name: 'FormEditGroup',
                          meta: {
                            label: 'Form Edit',
                            hideInBreadcrumbs: true
                          },
                          component: FormCreate,
                        },
                        {
                          path: 'answers',
                          name: 'FormAnswersGroup',
                          meta: {
                            label: 'Form Answers',
                            requiresGroupUser: true,
                          },
                          component: FormAnswers
                        },
                        {
                          path: 'fill_in',
                          component: {
                            render() {
                              return h(resolveComponent('router-view'));
                            },
                          },
                          meta: {
                            hideInBreadcrumbs: true,
                          },
                          children: [
                            {
                              path: ':answerId',
                              name: "AnswerGroupEdit",
                              meta: {
                                paramKey: 'answerId',
                              },
                              component: Form,
                            },
                            {
                              name: 'FormGroupFill',
                              meta: {
                                label: 'Form Fill',
                                isOpenAccess: true,
                              },
                              path: '',
                              component: Form,
                            }
                          ]
                        },
                        {
                          path: 'cards',
                          name: 'FormCardsGroup',
                          component: FormAnswersAsCards,
                        },
                      ]
                    },
                  ],
                },
              ]
            },
          ],
        },
      ]
    },
    {
      path: '/users',
      name: '',
      meta: {
        label: 'Users View',
        requiresAdmin: true,
      },
      component: TheContainer,
      children: [
        {
          path: '',
          meta: {
            label: 'Users View',
            requiresAdmin: true,
          },
          name: 'UsersView',
          component: UsersView,
        },
        {
          path: ':userId',
          name: 'UserView',
          meta: {
            paramKey: 'userId',
            requiresAdmin: true,
          },
          component: UserView,
        },
      ]
    },
    {
      path: '/account',
      name: 'account',
      meta: {
        isOpenAccess: true,
      },
      component: {
        render() {
          return h(resolveComponent('router-view'));
        },
      },
      children: [
        {
          path: 'login',
          name: 'Login',
          component: Login,
        },
        {
          path: 'login_management',
          name: 'ManagementLogin',
          component: Login,
          props: {
            override_mz_branding: true
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      component: TheContainer,
      children: [
        {
          path: '*',
          name: 'NotFound',
          component: Page404,
        }
      ]
    }
  ]
}


